/**
 * usePlans Hook
 * Manages plan CRUD operations with loading and error states
 */

import { useState, useEffect, useCallback } from 'react';
import { planService } from '../services/plan.service';
import type { Plan, PlanData, PlanStatus } from '../types';

interface UsePlansResult {
  plans: Plan[];
  isLoading: boolean;
  error: string | null;
  refreshPlans: () => Promise<void>;
  createPlan: (planData: PlanData) => Promise<Plan | null>;
  updatePlan: (planId: string, planData: Partial<PlanData>) => Promise<Plan | null>;
  updatePlanStatus: (planId: string, status: PlanStatus) => Promise<Plan | null>;
  deletePlan: (planId: string) => Promise<boolean>;
}

export function usePlans(): UsePlansResult {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all plans
   */
  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await planService.getAllPlans();
      setPlans(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch plans';
      setError(errorMessage);
      console.error('Error fetching plans:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load plans on mount
   */
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  /**
   * Refresh plans
   */
  const refreshPlans = useCallback(async () => {
    await fetchPlans();
  }, [fetchPlans]);

  /**
   * Create a new plan
   */
  const createPlan = useCallback(async (planData: PlanData): Promise<Plan | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await planService.createPlan(planData);
      setPlans(prev => [response.plan, ...prev]);
      return response.plan;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create plan';
      setError(errorMessage);
      console.error('Error creating plan:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update a plan
   */
  const updatePlan = useCallback(
    async (planId: string, planData: Partial<PlanData>): Promise<Plan | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const updatedPlan = await planService.updatePlan(planId, planData);
        setPlans(prev =>
          prev.map(plan => (plan.id === planId ? updatedPlan : plan))
        );
        return updatedPlan;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update plan';
        setError(errorMessage);
        console.error('Error updating plan:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Update plan status
   */
  const updatePlanStatus = useCallback(
    async (planId: string, status: PlanStatus): Promise<Plan | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const updatedPlan = await planService.updatePlanStatus(planId, status);
        setPlans(prev =>
          prev.map(plan => (plan.id === planId ? updatedPlan : plan))
        );
        return updatedPlan;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update plan status';
        setError(errorMessage);
        console.error('Error updating plan status:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Delete a plan
   */
  const deletePlan = useCallback(async (planId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await planService.deletePlan(planId);
      setPlans(prev => prev.filter(plan => plan.id !== planId));
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete plan';
      setError(errorMessage);
      console.error('Error deleting plan:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    plans,
    isLoading,
    error,
    refreshPlans,
    createPlan,
    updatePlan,
    updatePlanStatus,
    deletePlan,
  };
}